import React from "react";
import { Container } from "../components/ui/Container";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/Button";
import { trackTryKnoothClick } from "../lib/analytics";

const Tag = ({ children }) => {
  return (
    <span className="text-[16px] font-normal leading-[100%] text-[#412F14] bg-white border border-black/10 rounded px-[6px] py-[4px] w-fit">
      {children}
    </span>
  );
};

const CheckIcon = ({ className }) => {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 20 20"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.707-9.707a1 1 0 0 0-1.414-1.414L9 10.172 7.707 8.879a1 1 0 1 0-1.414 1.414l2 2a1 1 0 0 0 1.414 0l4-4Z"
      />
    </svg>
  );
};

const Bullet = ({ children }) => {
  return (
    <li className="flex items-start gap-2 text-[14px] md:text-[16px] font-normal leading-[140%] text-[#412F14]/70">
      <span className="flex-shrink-0 text-[#412F14]/50 mt-[2px]">
        <CheckIcon />
      </span>
      <span>{children}</span>
    </li>
  );
};

const Section = ({ tag, children }) => {
  return (
    <section className="mt-[32px] border-t border-black/10 pt-[80px] first:mt-0 first:border-t-0 first:pt-0">
      <Tag>{tag}</Tag>
      <div className="mt-[36px] text-[16px] md:text-[18px] leading-[160%] text-[#666666]">
        {children}
      </div>
    </section>
  );
};

const TermsOfService = () => {
  return (
    <div id="top" className="min-h-screen bg-[#FBFBFD]">
      <main className="pt-[280px]">
        <Container>
          <div className="text-center">
            <h1
              className="text-6xl md:text-8xl lg:text-[96px] font-[900] tracking-[-0.02em] leading-none text-transparent bg-clip-text pb-2"
              style={{ backgroundImage: "linear-gradient(180deg, #31220D 57%, #666666 100%)" }}
            >
              Terms of Service
              <br />
              for knooth
            </h1>
            <p className="mt-[16px] text-[14px] md:text-[16px] font-normal leading-[140%] text-[#666666]">
              Last updated: 21.01.2026
            </p>
          </div>

          <div className="mt-[80px] max-w-[1000px] mx-auto space-y-0">
            <Section tag="Acceptance of Terms">
              <p className="font-semibold text-[#666666]">
                By downloading, installing, or using knooth ("the App"), you agree to these Terms
                of Service ("Terms").
              </p>
              <p className="mt-[16px]">
                If you do not agree to these Terms, do not use the App.
              </p>
            </Section>

            <Section tag="Description of the Service">
              <p className="font-semibold text-[#666666]">
                knooth is a local-first application that enables content creation and editing, including optional on-device AI-powered features.
              </p>
              <ul className="mt-[12px] space-y-3">
                <Bullet>All processing happens locally on your device</Bullet>
                <Bullet>knooth does not require an account</Bullet>
                <Bullet>knooth does not upload or store your content on our servers</Bullet>
              </ul>
            </Section>

            <Section tag="On-Device AI Features">
              <p className="font-semibold text-[#666666]">
                knooth includes AI functionality for tasks such as:
              </p>
              <ul className="mt-[12px] space-y-3">
                <Bullet>Caption generation</Bullet>
                <Bullet>Speech cleanup and disfluency removal</Bullet>
              </ul>
              <p className="font-semibold text-[#666666] mt-[16px]">
                AI Processing Principles
              </p>
              <ul className="mt-[12px] space-y-3">
                <Bullet>AI processing occurs entirely on the user’s device</Bullet>
                <Bullet>No data is transmitted to third-party services</Bullet>
                <Bullet>No user content is retained by the developer</Bullet>
              </ul>
              <p className="font-regular text-[#666666] mt-[16px]">
                The AI model embedded in the app is licensed for commercial use.
              </p>
            </Section>

            <Section tag="Eligibility">
              <p className="font-semibold text-[#666666]">knooth is rated 4+ on the App Store and is suitable for all ages.</p>
              <p className="mt-[16px]">By using knooth, you confirm that you meet Apple's age requirements for downloading apps.</p>
            </Section>

            <Section tag="Your Content & Ownership">
              <p className="font-semibold text-[#666666]">
                You retain full ownership of all content you create using knooth, including (but
                not limited to):
              </p>
              <ul className="mt-[12px] space-y-3">
                <Bullet>Recordings</Bullet>
                <Bullet>Files</Bullet>
                <Bullet>Assets</Bullet>
                <Bullet>Projects</Bullet>
                <Bullet>Exports</Bullet>
              </ul>
              <p className="mt-[16px]">We do not claim ownership over your content.</p>
              <p className="mt-[16px]">knooth does not access, collect, or store your content.</p>
            </Section>

            <Section tag="Local Data Processing & Responsibility">
              <p className="font-semibold text-[#666666]">Because knooth operates locally:</p>
              <ul className="mt-[12px] space-y-3">
                <Bullet>You are responsible for managing, backing up, and securing your files</Bullet>
                <Bullet>Deleting the app may result in loss of locally stored content</Bullet>
                <Bullet>Exporting or sharing content is entirely under your control</Bullet>
              </ul>
              <p className="font-semibold text-[#666666] mt-[16px]">AI-Generated Output</p>
              <ul className="mt-[12px] space-y-3">
                <Bullet>knooth includes optional AI-powered features</Bullet>
                <Bullet>AI-generated output may not always be accurate, complete, or error-free</Bullet>
                <Bullet>You are responsible for reviewing and validating AI-generated content before use</Bullet>
              </ul>
              <p className="mt-[16px]">
                We are not responsible for data loss caused by device failure, user actions, or
                operating system behavior.
              </p>
            </Section>

            <Section tag="Privacy">
              <p className="font-semibold text-[#666666]">Your privacy is important to us.</p>
              <ul className="mt-[12px] space-y-3">
                <Bullet>knooth does not require an account</Bullet>
                <Bullet>knooth does not collect or store your content on our servers</Bullet>
                <Bullet>
                  knooth may collect limited, anonymous usage analytics to improve performance and
                  features
                </Bullet>
                <Bullet>knooth does not show ads</Bullet>
              </ul>
              <p className="mt-[16px] font-semibold text-[#666666]">Crash Reports & Diagnostic Logs</p>
              <p className="mt-[8px]">
                knooth collects anonymous usage and crash diagnostics to improve app performance
                and reliability.
              </p>
              <p className="mt-[16px]">
                Apple may also provide anonymized crash reports if you’ve enabled diagnostic
                sharing on your device.
              </p>
              <p className="mt-[16px]">
                No personal data or app content is collected, and diagnostics are never used for
                tracking or profiling.
              </p>
              <p className="mt-[16px]">
                For full details, please review our Privacy Policy.
              </p>
            </Section>

            <Section tag="Limitations on Use">
              <p className="font-semibold text-[#666666]">
                We grant you a limited, non-exclusive, non-transferable, revocable license to use
                knooth for personal or professional purposes, subject to these Terms.
              </p>
              <p className="mt-[16px] font-semibold text-[#666666]">You may not:</p>
              <ul className="mt-[12px] space-y-3">
                <Bullet>Reverse engineer or attempt to extract source code (except where permitted by law)</Bullet>
                <Bullet>Redistribute or resell the App</Bullet>
                <Bullet>Use the App for unlawful purposes</Bullet>
              </ul>
            </Section>

            <Section tag="Updates & Changes">
              <p className="font-semibold text-[#666666]">We may:</p>
              <ul className="mt-[12px] space-y-3">
                <Bullet>Release updates or improvements</Bullet>
                <Bullet>Modify or discontinue features</Bullet>
                <Bullet>Fix bugs or performance issues</Bullet>
              </ul>
              <p className="mt-[16px]">Updates may be required to continue using the App.</p>
            </Section>

            <Section tag="Disclaimer & Availability">
              <p className="font-semibold text-[#666666]">knooth is provided "as is" and "as available."</p>
              <p className="mt-[16px] font-semibold text-[#666666]">We do not guarantee that:</p>
              <ul className="mt-[12px] space-y-3">
                <Bullet>The App will be error-free</Bullet>
                <Bullet>The App will meet all your requirements</Bullet>
                <Bullet>The App will operate uninterrupted on all devices</Bullet>
              </ul>
              <p className="mt-[16px]">Use the App at your own discretion and risk.</p>
            </Section>

            <Section tag="Limitation of Liability">
              <p className="font-semibold text-[#666666]">To the maximum extent permitted by law:</p>
              <ul className="mt-[12px] space-y-3">
                <Bullet>We are not liable for indirect, incidental, or consequential damages</Bullet>
                <Bullet>We are not responsible for data loss, business interruption, or device issues</Bullet>
                <Bullet>Our total liability will not exceed the amount paid for the App (if any)</Bullet>
              </ul>
            </Section>

            <Section tag="Termination">
              <p>You may stop using knooth at any time by uninstalling the App.</p>
              <p className="mt-[16px] font-semibold text-[#666666]">We may suspend or terminate access if:</p>
              <ul className="mt-[12px] space-y-3">
                <Bullet>These Terms are violated</Bullet>
                <Bullet>Required by law</Bullet>
                <Bullet>Necessary to protect the App or users</Bullet>
              </ul>
            </Section>

            <Section tag="Governing Law">
              <p>
                These Terms are governed by the laws of India, without regard to conflict-of-law
                principles.
              </p>
            </Section>

            <Section tag="Changes to these Terms">
              <p className="font-semibold text-[#666666]">We may update these Terms from time to time.</p>
              <p className="mt-[16px]">
                Any changes will be posted with an updated "Last updated" date.
              </p>
              <p className="mt-[16px]">
                Continued use of the App after changes means you accept updated Terms.
              </p>
            </Section>

            <Section tag="Contact Us">
              <p className="font-semibold text-[#666666]">
                If you have questions about this policy or app behavior, reach us at:
              </p>
              <ul className="mt-[12px] space-y-3">
                <Bullet>
                  Email:&nbsp;
                  <a href="mailto:support@knooth.com" className="text-[#31220D] hover:underline">
                    support@knooth.com
                  </a>
                </Bullet>
                <Bullet>
                  Website:&nbsp;
                  <a
                    href="https://www.knooth.com"
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#31220D] hover:underline"
                  >
                    https://www.knooth.com
                  </a>
                </Bullet>
              </ul>
              <p className="mt-[24px] text-[16px] font-normal leading-[1.6] text-[#412F14]/70">
                For more details, visit Apple&apos;s{" "}
                <a
                  href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/"
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#31220D] hover:underline"
                >
                  Licensed Application End User License Agreement
                </a>.
              </p>
            </Section>
          </div>

          {/* Closing statement + CTA */}
          <div className="mt-[120px] text-center pb-[140px]">
            <h2
              className="text-4xl md:text-5xl lg:text-[56px] font-[900] tracking-[-0.02em] leading-none text-transparent bg-clip-text"
              style={{ backgroundImage: "linear-gradient(180deg, #31220D 57%, #666666 100%)" }}
            >
              knooth is a local-first app.
              <br />
              You own your content.
              <br />
              We don&apos;t collect your data.
            </h2>

            <div className="mt-[40px] flex flex-col items-center gap-[10px]">
              <Button
                href="https://apps.apple.com/app/knooth/id6756659300?mt=12"
                target="_blank"
                rel="noreferrer"
                data-analytics="try-knooth"
                data-location="tos-cta"
                onClick={() => trackTryKnoothClick("tos-cta")}
                className="w-[176px] h-[44px] px-6 py-[10px] rounded-full text-white font-bold text-[24px] hover:shadow-lg transition-all border-none leading-none flex items-center justify-center"
                style={{ background: "linear-gradient(180deg, #2F00EC 57%, #C8A5FF 100%)" }}
              >
                Try knooth
              </Button>
              <p className="text-[14px] font-normal leading-[1.4] text-[#31220D]">1 week free for new subscribers.</p>
            </div>
          </div>
        </Container>
      </main>

      <Footer />
    </div>
  );
};

export { TermsOfService };
